import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useOrgPermissions } from "@/hooks/useOrgPermissions";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { KeyRound, Plus, Copy, Ban, Loader2, Lock, Check, Terminal } from "lucide-react";
import { Link } from "react-router-dom";

interface ApiKeyRow {
  id: string;
  name: string;
  key_prefix: string;
  scopes: string[];
  created_at: string;
  last_used_at: string | null;
  revoked_at: string | null;
}

export function ApiKeysSettings() {
  const { orgId, can } = useOrgPermissions();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const isAdmin = can("organogram", "full");

  const [createOpen, setCreateOpen] = useState(false);
  const [newKeyName, setNewKeyName] = useState("");
  const [creating, setCreating] = useState(false);
  const [mintedKey, setMintedKey] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const { data: billingSummary } = useQuery({
    queryKey: ["org-billing-summary", orgId],
    enabled: !!orgId,
    queryFn: async () => {
      const { data } = await (supabase.rpc("get_org_billing_summary", { p_org_id: orgId } as any) as any);
      return data as { plan: string } | null;
    },
  });

  const isEnterprise = billingSummary?.plan === "enterprise";

  const { data: keys, isLoading } = useQuery({
    queryKey: ["api-keys", orgId],
    enabled: !!orgId && isEnterprise && isAdmin,
    queryFn: async () => {
      const { data, error } = await (supabase
        .from("api_keys" as any)
        .select("id, name, key_prefix, scopes, created_at, last_used_at, revoked_at")
        .eq("org_id", orgId!)
        .order("created_at", { ascending: false }) as any);
      if (error) throw error;
      return (data ?? []) as ApiKeyRow[];
    },
  });

  const handleCreate = async () => {
    if (!orgId || !newKeyName.trim()) return;
    setCreating(true);
    try {
      const { data, error } = await (supabase.rpc("create_api_key" as any, {
        p_org_id: orgId,
        p_name: newKeyName.trim(),
        p_scopes: ["read"],
      }) as any);
      if (error) throw error;
      const row = Array.isArray(data) ? data[0] : data;
      setMintedKey(row.plaintext_key);
      setNewKeyName("");
      queryClient.invalidateQueries({ queryKey: ["api-keys", orgId] });
    } catch (err) {
      toast({ title: "Couldn't create key", description: (err as Error).message, variant: "destructive" });
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (id: string) => {
    try {
      const { error } = await (supabase.rpc("revoke_api_key" as any, { p_key_id: id }) as any);
      if (error) throw error;
      toast({ title: "Key revoked" });
      queryClient.invalidateQueries({ queryKey: ["api-keys", orgId] });
    } catch (err) {
      toast({ title: "Couldn't revoke key", description: (err as Error).message, variant: "destructive" });
    }
  };

  const copyMintedKey = () => {
    if (!mintedKey) return;
    navigator.clipboard.writeText(mintedKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const closeCreateDialog = () => {
    setCreateOpen(false);
    setMintedKey(null);
    setCopied(false);
  };

  if (!isAdmin) {
    return (
      <Card className="max-w-2xl">
        <CardContent className="py-10 text-center text-sm text-muted-foreground">
          Only an institution admin can manage API keys.
        </CardContent>
      </Card>
    );
  }

  if (!isEnterprise) {
    return (
      <Card className="max-w-2xl">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Lock className="w-4 h-4 text-muted-foreground" /> API Access
          </CardTitle>
          <CardDescription>
            Programmatic access to your media registry, press releases, and coverage scores is an Enterprise feature.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Link to="/pricing">
            <Button>Upgrade to Enterprise</Button>
          </Link>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6 max-w-2xl">
      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-4">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <KeyRound className="w-4 h-4 text-primary" /> API Keys
            </CardTitle>
            <CardDescription>
              Read-only programmatic access to journalists, press releases, and coverage scores.
            </CardDescription>
          </div>
          <Dialog open={createOpen} onOpenChange={(open) => (open ? setCreateOpen(true) : closeCreateDialog())}>
            <DialogTrigger asChild>
              <Button size="sm" className="gap-1.5 shrink-0">
                <Plus className="w-4 h-4" /> New key
              </Button>
            </DialogTrigger>
            <DialogContent>
              {!mintedKey ? (
                <>
                  <DialogHeader>
                    <DialogTitle>Create API key</DialogTitle>
                    <DialogDescription>
                      Give it a name that identifies where it will be used, e.g. "Intranet dashboard" or "Comms CRM sync".
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-2 py-2">
                    <Label htmlFor="key-name">Key name</Label>
                    <Input
                      id="key-name"
                      value={newKeyName}
                      onChange={(e) => setNewKeyName(e.target.value)}
                      placeholder="Intranet dashboard"
                    />
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={closeCreateDialog}>Cancel</Button>
                    <Button onClick={handleCreate} disabled={creating || !newKeyName.trim()}>
                      {creating && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                      Create key
                    </Button>
                  </DialogFooter>
                </>
              ) : (
                <>
                  <DialogHeader>
                    <DialogTitle>Your new API key</DialogTitle>
                    <DialogDescription>
                      Copy it now — for your security, we can't show it to you again after this dialog closes.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="flex items-center gap-2 py-2">
                    <code className="flex-1 text-xs bg-muted px-3 py-2.5 rounded-lg border border-border font-mono break-all">
                      {mintedKey}
                    </code>
                    <Button size="icon" variant="outline" onClick={copyMintedKey} className="shrink-0">
                      {copied ? <Check className="w-4 h-4 text-primary" /> : <Copy className="w-4 h-4" />}
                    </Button>
                  </div>
                  <DialogFooter>
                    <Button onClick={closeCreateDialog}>Done</Button>
                  </DialogFooter>
                </>
              )}
            </DialogContent>
          </Dialog>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-sm text-muted-foreground py-4">Loading…</div>
          ) : !keys || keys.length === 0 ? (
            <div className="text-sm text-muted-foreground py-6 text-center border border-dashed border-border rounded-lg">
              No API keys yet. Create one to start pulling data programmatically.
            </div>
          ) : (
            <div className="space-y-2">
              {keys.map((k) => (
                <div
                  key={k.id}
                  className="flex items-center justify-between gap-3 px-4 py-3 rounded-lg border border-border"
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium truncate">{k.name}</span>
                      {k.revoked_at && <Badge variant="destructive" className="text-[10px]">Revoked</Badge>}
                    </div>
                    <code className="text-[11px] text-muted-foreground">{k.key_prefix}…</code>
                    <div className="text-[11px] text-muted-foreground mt-0.5">
                      Created {new Date(k.created_at).toLocaleDateString()}
                      {k.last_used_at && ` · Last used ${new Date(k.last_used_at).toLocaleDateString()}`}
                      {!k.last_used_at && " · Never used"}
                    </div>
                  </div>
                  {!k.revoked_at && (
                    <Button size="sm" variant="ghost" className="text-destructive shrink-0" onClick={() => handleRevoke(k.id)}>
                      <Ban className="w-3.5 h-3.5 mr-1.5" /> Revoke
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm flex items-center gap-2">
            <Terminal className="w-4 h-4 text-muted-foreground" /> Quick start
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-xs text-muted-foreground">
            Every key is scoped to your institution only. Available endpoints:
          </p>
          <ul className="text-xs text-muted-foreground list-disc pl-5 space-y-1">
            <li><code>GET /public-api/journalists</code></li>
            <li><code>GET /public-api/press-releases</code></li>
            <li><code>GET /public-api/coverage-scores</code></li>
          </ul>
          <pre className="text-[11px] bg-muted rounded-lg p-3 overflow-x-auto">
{`curl -H "Authorization: Bearer cbk_live_..." \\
  https://<project>.supabase.co/functions/v1/public-api/journalists`}
          </pre>
        </CardContent>
      </Card>
    </div>
  );
}
